import { getEnv } from "./env";

const GOOGLE_RECAPTCHA_DIV_ID = "google-recaptcha";
const ACTION_FN_QUEUE: (() => Promise<void>)[] = [];
const SITE_KEYS = {
  development: "",
  staging: "6LdsCGUhAAAAAGAyWZHg3MVOrpYfc5nY8VILdQlj",
  production: "6LeuDWUhAAAAAFyDoI-FL327Q1gPcj1HurJcXks5"
};
let SITE_KEY: string | null = SITE_KEYS[getEnv(true)];
let INITIALIZED = false;
// if in 15s the script is not loaded, we will resolve all the pending `execute` calls with `undefined` and log an error.
// The timeout is set quite high as if this script fails to load, it will break the login flow
const TIMEOUT_MS = 15000;
let loadingTimeout: number | null = null;

declare global {
  interface Window {
    grecaptcha?: {
      execute: (siteKey: string, options: { action: string }) => Promise<string | undefined>;
    };
    onRecaptchaLoaded: () => void;
  }
}

type Action =
  | "signIn"
  | "signUp"
  | "changePassword"
  | "register"
  | "verify"
  | "sendVerify"
  | "recoverPassword"
  | "resetPassword"
  | "tokenCheck"
  | "tokenRedeem"
  | "paymentLinksSession"
  | "paymentLinksTransfer"
  | "resolutionLinksSession";

const processQueue = () => {
  ACTION_FN_QUEUE.forEach(async (fn) => {
    await fn();
  });
};

const delayExecutionUntilScriptLoaded = async (action: Action): Promise<string | undefined> => {
  return new Promise((resolve) => {
    ACTION_FN_QUEUE.push(async () => {
      const recaptchaToken = await execute(action);
      resolve(recaptchaToken);
    });
  });
};

window.onRecaptchaLoaded = () => {
  console.log("ℹ️ Google Recaptcha ready");
  INITIALIZED = true;
  if (loadingTimeout) window.clearTimeout(loadingTimeout);
  void processQueue();
};

/** Initializes the Google recaptcha script if needed */
function init() {
  // if we're already initializing the script, or the script is already initialized, or the site key is not set, do nothing
  if (loadingTimeout !== null || INITIALIZED || !SITE_KEY) return;

  const grecaptchaScript = document.createElement("script");
  grecaptchaScript.id = GOOGLE_RECAPTCHA_DIV_ID;
  grecaptchaScript.async = true;
  grecaptchaScript.setAttribute("data-site-key", SITE_KEY);
  grecaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}&onload=onRecaptchaLoaded`;
  document.head.appendChild(grecaptchaScript);

  // in the worst case where the script does not load within TIMEOUT_MS, we will resolve all the pending `execute` calls with `undefined` and throw an error
  // so it's logged through HoneyComb telemetry
  loadingTimeout = window.setTimeout(() => {
    SITE_KEY = null;
    processQueue(); // will resolve all the pending `execute` calls with `undefined`
    throw Error(
      "🔴 Timeout loading Google Recaptcha Script. Returning `undefined` to any `execute` calls, which will probably break things."
    );
  }, TIMEOUT_MS);
}

/** Triggers a request to Google, which returns a recaptcha challenge token
 * @param action A name for the user action being performed. Organizes data in the recaptcha admin tool.
 * @returns A promise resolving to the recaptcha challenge token
 */
async function execute(action: Action): Promise<string | undefined> {
  // when we're in an environment that doesn't have Google Recaptcha enabled, or the script failed to load
  if (!SITE_KEY) return undefined;
  if (!INITIALIZED || !window.grecaptcha) {
    // init just in case the script is not initializing yet
    init();
    // we store the action in the queue to be executed after the script is initialized
    return delayExecutionUntilScriptLoaded(action);
  }
  return await window.grecaptcha.execute(SITE_KEY, { action });
}

const GoogleRecaptcha = {
  init,
  execute
};

export default GoogleRecaptcha;
